import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { gameDetails } from "../App";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const USERS_PER_PAGE = 10;

interface BlockhopGameData {
    levelIndex: number;
    playTime: number;
    health: number;
    isWin: boolean;
    isLose: boolean;
    moveCount: number;
}

interface BlockhopSession {
    gameDataList: BlockhopGameData[];
}

const UsersPageBlockhop: React.FC = () => {
    const navigate = useNavigate();
    const { gameData } = useGameData();
    const { t } = useLanguage();
    const sessions = gameData["game9"] || [];
    const [currentPage, setCurrentPage] = useState(1);
    const [userIdFilter, setUserIdFilter] = useState<string>("");
    const [purchaseFilter, setPurchaseFilter] = useState<boolean | null>(null);
    const [confirmFilter, setConfirmFilter] = useState<boolean | null>(null);

    const handleBack = () => {
        navigate("/");
    };

    const formatSessionTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        let result = "";
        if (hours > 0) result += `${hours}${t("h")} `;
        if (minutes > 0) result += `${minutes}${t("m")} `;
        if (remainingSeconds > 0 || result === "") result += `${remainingSeconds}${t("s")}`;
        return result.trim();
    };

    const filteredSessions = sessions.filter((session) => {
        return (
            (!userIdFilter || session.userId.toLowerCase().includes(userIdFilter.toLowerCase())) &&
            (purchaseFilter === null || session.purchaseClicked === purchaseFilter) &&
            (confirmFilter === null || session.confirmClicked === confirmFilter)
        );
    });

    const totalPages = Math.ceil(filteredSessions.length / USERS_PER_PAGE);
    const startIndex = (currentPage - 1) * USERS_PER_PAGE;
    const endIndex = startIndex + USERS_PER_PAGE;
    const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

    // Grafik Verileri
    const statsData = {
        labels: [t("total_users"), t("avg_session"), t("purchases"), t("confirmations")],
        datasets: [
            {
                label: t("statistics"),
                data: [
                    filteredSessions.length,
                    filteredSessions.reduce((sum, session) => sum + (session.sessionTime || 0), 0) / filteredSessions.length || 0,
                    filteredSessions.filter(session => session.purchaseClicked).length,
                    filteredSessions.filter(session => session.confirmClicked).length,
                ],
                backgroundColor: ["rgba(75, 192, 192, 0.7)", "rgba(54, 162, 235, 0.7)", "rgba(255, 159, 64, 0.7)", "rgba(255, 99, 132, 0.7)"],
                borderColor: ["rgba(75, 192, 192, 1)", "rgba(54, 162, 235, 1)", "rgba(255, 159, 64, 1)", "rgba(255, 99, 132, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const purchaseData = {
        labels: [t("purchased"), t("not_purchased")],
        datasets: [
            {
                label: t("purchase"),
                data: [
                    filteredSessions.filter((s) => s.purchaseClicked).length,
                    filteredSessions.filter((s) => !s.purchaseClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const confirmData = {
        labels: [t("confirmed"), t("not_confirmed")],
        datasets: [
            {
                label: t("confirm"),
                data: [
                    filteredSessions.filter((s) => s.confirmClicked).length,
                    filteredSessions.filter((s) => !s.confirmClicked).length,
                ],
                backgroundColor: ["rgba(0, 172, 193, 0.7)", "rgba(255, 87, 34, 0.7)"],
                borderColor: ["rgba(0, 172, 193, 1)", "rgba(255, 87, 34, 1)"],
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: "top" as const, labels: { font: { size: 12 } } },
            title: {
                display: true,
                font: { size: 16, weight: "600" as const },
                color: "#1f2937",
            },
            tooltip: {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
            },
        },
        scales: {
            y: { beginAtZero: true, grid: { color: "rgba(0, 0, 0, 0.05)" } },
            x: { grid: { display: false } },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Navbar />
            <main className="mt-36 mb-16 flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center justify-between"
                >
                    <div className="flex items-center space-x-4">
                        <img src={gameDetails["game9"]?.logo || ""} alt="Blockhop" className="w-12 h-12 rounded-full" />
                        <h2 className="text-3xl font-bold text-gray-800">Blockhop {t("users")}</h2>
                    </div>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleBack}
                        className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                    >
                        {t("back_to_dashboard")}
                    </motion.button>
                </motion.div>

                {/* Grafik Bölümü */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={statsData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("general_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={purchaseData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("purchase_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        className="bg-white rounded-xl shadow-lg p-5"
                    >
                        <Bar
                            data={confirmData}
                            options={{
                                ...chartOptions,
                                plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, text: t("confirm_stats"), font: { size: 16, weight: "bold" }, color: "#1f2937" } },
                            }}
                            height={200}
                        />
                    </motion.div>
                </div>

                {/* Filtreleme Bölümü */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{t("filters")}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("user_id")}</label>
                            <input
                                type="text"
                                value={userIdFilter}
                                onChange={(e) => setUserIdFilter(e.target.value)}
                                placeholder={t("eg_user_id")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("purchase_status")}</label>
                            <select
                                value={purchaseFilter === null ? "" : purchaseFilter ? "true" : "false"}
                                onChange={(e) => setPurchaseFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("purchased")}</option>
                                <option value="false">{t("not_purchased")}</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t("confirm_status")}</label>
                            <select
                                value={confirmFilter === null ? "" : confirmFilter ? "true" : "false"}
                                onChange={(e) => setConfirmFilter(e.target.value === "" ? null : e.target.value === "true")}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">{t("all")}</option>
                                <option value="true">{t("confirmed")}</option>
                                <option value="false">{t("not_confirmed")}</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Kullanıcı Kartları */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {paginatedSessions.length > 0 ? (
                        paginatedSessions.map((session) => {
                            const gameData: BlockhopSession = session.GameData
                                ? JSON.parse(session.GameData)
                                : { gameDataList: [] };
                            return (
                                <motion.div
                                    key={session.userId}
                                    className="bg-white rounded-xl shadow-lg p-5 hover:shadow-xl transition-shadow duration-200"
                                    variants={cardVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                                        {" "}
                                        <span className="relative group">
                                            <span className="text-gray-800">#</span>
                                            <span className="absolute left-full ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 px-2 py-1 rounded text-sm text-gray-700">
                                                {session.userId}
                                            </span>
                                        </span>
                                    </h3>
                                    <div className="space-y-2 text-gray-600">
                                        <p>
                                            <span className="font-medium text-blue-600">{t("session_time")}:</span>{" "}
                                            {formatSessionTime(session.sessionTime)}
                                        </p>
                                        <p>
                                            <span className="font-medium text-red-600">{t("max_level")}:</span> {session.maxLevel}
                                        </p>
                                        <p>
                                            <span className="font-medium text-green-600">{t("confirm_clicked")}:</span>{" "}
                                            {session.confirmClicked === false ? t("not_confirmed") : t("confirmed")}
                                        </p>
                                        <p>
                                            <span className="font-medium text-purple-600">{t("purchase_clicked")}:</span>{" "}
                                            {session.purchaseClicked ? t("yes") : t("no")}
                                        </p>
                                    </div>

                                    {/* Game Data Accordion */}
                                    <div className="mt-4">
                                        <div className="border rounded-lg overflow-hidden">
                                            <button 
                                                className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                                onClick={() => {
                                                    const element = document.getElementById(`game-data-${session.userId}`);
                                                    if (element) {
                                                        element.classList.toggle('hidden');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <h4 className="text-md font-semibold text-gray-700">{t("game_data")}</h4>
                                                    <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                        gameData.gameDataList.length > 0 
                                                        ? "bg-green-100 text-green-800" 
                                                        : "bg-red-100 text-red-800"
                                                    }`}>
                                                        {gameData.gameDataList.length > 0 ? t("data_available") : t("no_data_available")}
                                                    </span>
                                                </div>
                                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                                </svg>
                                            </button>
                                            <div id={`game-data-${session.userId}`} className="hidden p-3 bg-white">
                                                {gameData.gameDataList.length > 0 ? (
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm text-gray-600">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("level")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("play_time")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("health")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("result")}</th>
                                                                    <th className="py-2 px-3 text-left font-medium">{t("moves")}</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {gameData.gameDataList.map((level, index) => (
                                                                    <tr key={index} className="border-b">
                                                                        <td className="py-2 px-3">{level.levelIndex}</td>
                                                                        <td className="py-2 px-3">{level.playTime.toFixed(2)}{t("s")}</td>
                                                                        <td className="py-2 px-3">{level.health}</td>
                                                                        <td className="py-2 px-3">
                                                                            {level.isWin ? t("win") : level.isLose ? t("lose") : t("in_progress")}
                                                                        </td>
                                                                        <td className="py-2 px-3">{level.moveCount}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                ) : (
                                                    <p className="text-gray-500">{t("no_data_available")}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })
                    ) : (
                        <p className="col-span-full text-gray-500 text-center py-8">{t("no_user_data", { gameName: "Blockhop" })}</p>
                    )}
                </div>

                {/* Pagination Kontrolleri */}
                {totalPages > 1 && (
                    <div className="mt-8 flex items-center justify-center space-x-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-500 text-white hover:bg-blue-600"
                                }`}
                        >
                            {t("previous")}
                        </motion.button>
                        <span className="text-gray-700">
                            {t("page")} {currentPage} / {totalPages}
                        </span>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`py-2 px-4 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-500 text-white hover:bg-blue-600"
                                }`}
                        >
                            {t("next")}
                        </motion.button>
                    </div>
                )}
            </main>

            <footer className="bg-gray-800 py-4">
                <div className="max-w-7xl mx-auto px-4 text-center">
                    <p className="text-gray-300 text-sm">{t("footer", { year: new Date().getFullYear() })}</p>
                </div>
            </footer>
        </div>
    );
};

export default UsersPageBlockhop; 